import type { ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useRequireAuth } from "../../hooks/useRequireAuth";
import { useTenant } from "../../hooks/useTenant";
import { getRoleLabel, TRAINER_ROLES } from "../../lib/constants";
import type { AppRole } from "../../types/auth";
import { AppShell } from "../layout/AppShell";
import { Button } from "../ui/Button";
import { EmptyState } from "../ui/EmptyState";
import { FullPageLoader } from "../ui/FullPageLoader";

type ProtectedRouteProps = {
  allowedRoles?: readonly AppRole[];
  children: ReactNode;
};

export function ProtectedRoute({ allowedRoles, children }: ProtectedRouteProps) {
  const auth = useRequireAuth();
  const tenant = useTenant();

  if (!auth.isConfigured) {
    return (
      <EmptyState
        eyebrow="Configuration"
        title="Supabase is not configured yet."
      >
        Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY before using protected
        CoachGrid routes.
      </EmptyState>
    );
  }

  if (auth.loading || tenant.loading) {
    return <FullPageLoader label="Checking access" />;
  }

  if (!auth.user) {
    return <Navigate to="/login" replace />;
  }

  if (auth.profile?.status === "disabled") {
    return (
      <EmptyState
        eyebrow="Account disabled"
        title="This account is currently disabled."
      >
        Contact CoachGrid support or your platform administrator if this looks
        wrong.
      </EmptyState>
    );
  }

  if (tenant.error) {
    return (
      <AppShell>
        <EmptyState eyebrow="Workspace error" title="We could not load access.">
          {tenant.error}
        </EmptyState>
      </AppShell>
    );
  }

  if (!tenant.currentRole) {
    return (
      <AppShell>
        <EmptyState
          action={
            <Button onClick={() => void auth.signOut()} variant="secondary">
              Sign out
            </Button>
          }
          eyebrow="No active role"
          title="This account is not connected to an active workspace."
        >
          CoachGrid found your user account, but no active tenant membership.
        </EmptyState>
      </AppShell>
    );
  }

  if (allowedRoles && !allowedRoles.includes(tenant.currentRole)) {
    return (
      <AppShell>
        <EmptyState
          eyebrow="Access denied"
          title="This workspace area is not available to your role."
        >
          Your current role is {getRoleLabel(tenant.currentRole)}.
        </EmptyState>
      </AppShell>
    );
  }

  if (
    TRAINER_ROLES.includes(tenant.currentRole as (typeof TRAINER_ROLES)[number]) &&
    tenant.currentTenant?.status === "suspended"
  ) {
    return (
      <AppShell>
        <EmptyState
          eyebrow="Workspace suspended"
          title="This workspace is currently suspended."
        >
          Contact CoachGrid support or your platform administrator before using
          trainer workspace tools.
        </EmptyState>
      </AppShell>
    );
  }

  return children;
}
